import { Eyebrow, Segmented, TextField } from '../../../ds/components';
import type { CreatePropertyBody, PatchPropertyBody, PropertyRecord, PropertyType } from '../types';
import { parseInteger } from '../format';
import { DeveloperPicker } from './DeveloperPicker';
import styles from './wizard.module.css';

export interface BasicsDraft {
  name: string;
  /** „Straße und Hausnummer" as one field; split into `street` + `houseNumber` for the API. */
  address: string;
  postalCode: string;
  city: string;
  propertyType: PropertyType;
  developer: { id: string; name: string; initials: string } | null;
  unitsEstimated: string;
}

export type BasicsErrors = Partial<Record<'name' | 'developerId' | 'unitsEstimated' | 'postalCode', string>>;

const NAME_REQUIRED = 'Bitte einen Arbeitstitel angeben.';
const DEVELOPER_REQUIRED = 'Bitte einen Verkäufer oder Bauträger wählen.';
const NUMBER_REQUIRED = 'Bitte eine Zahl angeben.';

function initialsOf(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

export function emptyDraft(): BasicsDraft {
  return {
    name: '',
    address: '',
    postalCode: '',
    city: '',
    propertyType: 'existing',
    developer: null,
    unitsEstimated: '',
  };
}

export function draftFromRecord(record: PropertyRecord): BasicsDraft {
  return {
    name: record.name,
    address: [record.street, record.houseNumber].filter(Boolean).join(' '),
    postalCode: record.postalCode ?? '',
    city: record.city ?? '',
    propertyType: record.propertyType,
    developer: record.developer
      ? { id: record.developer.id, name: record.developer.name, initials: initialsOf(record.developer.name) }
      : null,
    unitsEstimated: record.fields.unitsEstimated === null ? '' : String(record.fields.unitsEstimated),
  };
}

/** „Donaulände 9" → street „Donaulände", house number „9"; „Am Ring 12 a" → „Am Ring" / „12 a". */
export function splitAddress(address: string): { street: string | null; houseNumber: string | null } {
  const trimmed = address.trim().replace(/\s+/g, ' ');
  if (trimmed === '') return { street: null, houseNumber: null };
  const match = /^(.*?)[\s,]+(\d+[\w\-/]*(?:\s?[a-zA-Z])?)$/.exec(trimmed);
  if (!match || !match[1]) return { street: trimmed, houseNumber: null };
  return { street: match[1].trim(), houseNumber: (match[2] ?? '').trim() || null };
}

export function validateDraft(draft: BasicsDraft): BasicsErrors {
  const errors: BasicsErrors = {};
  if (draft.name.trim() === '') errors.name = NAME_REQUIRED;
  if (draft.developer === null) errors.developerId = DEVELOPER_REQUIRED;
  if (draft.unitsEstimated.trim() !== '' && parseInteger(draft.unitsEstimated) === null)
    errors.unitsEstimated = NUMBER_REQUIRED;
  return errors;
}

export function createBodyFromDraft(draft: BasicsDraft): CreatePropertyBody {
  const { street, houseNumber } = splitAddress(draft.address);
  return {
    name: draft.name.trim(),
    street,
    houseNumber,
    postalCode: draft.postalCode.trim() || null,
    city: draft.city.trim() || null,
    propertyType: draft.propertyType,
    developerId: draft.developer?.id ?? '',
    unitsEstimated: draft.unitsEstimated.trim() === '' ? null : parseInteger(draft.unitsEstimated),
  };
}

/** The PATCH for one changed draft key (autosave from step 2 on, Objekt anlegen A3). */
export function patchForKey(key: keyof BasicsDraft, draft: BasicsDraft): PatchPropertyBody | null {
  switch (key) {
    case 'name':
      return draft.name.trim() === '' ? null : { name: draft.name.trim() };
    case 'address': {
      const { street, houseNumber } = splitAddress(draft.address);
      return { street, houseNumber };
    }
    case 'postalCode':
      return { postalCode: draft.postalCode.trim() || null };
    case 'city':
      return { city: draft.city.trim() || null };
    case 'propertyType':
      return { propertyType: draft.propertyType };
    case 'developer':
      return draft.developer ? { developerId: draft.developer.id } : null;
    case 'unitsEstimated': {
      if (draft.unitsEstimated.trim() === '') return { unitsEstimated: null };
      const value = parseInteger(draft.unitsEstimated);
      return value === null ? null : { unitsEstimated: value };
    }
  }
}

const PROPERTY_TYPES: { value: PropertyType; label: string }[] = [
  { value: 'existing', label: 'Bestand' },
  { value: 'new_build', label: 'Neubau' },
  { value: 'core_renovation', label: 'Kernsanierung' },
];

export interface BasicsFormProps {
  draft: BasicsDraft;
  errors: BasicsErrors;
  disabled?: boolean | undefined;
  onChange: (key: keyof BasicsDraft, draft: BasicsDraft) => void;
}

/** Objekt anlegen 01 „Worum geht es?" — the form card. */
export function BasicsForm({ draft, errors, disabled = false, onChange }: BasicsFormProps) {
  const set = <K extends keyof BasicsDraft>(key: K, value: BasicsDraft[K]) =>
    onChange(key, { ...draft, [key]: value });
  return (
    <section className={`${styles.card} ${styles.cardWide}`} aria-labelledby="wizard-basics">
      <h2 id="wizard-basics" className={styles.cardTitle}>
        Worum geht es?
      </h2>
      <p className={styles.cardText}>
        Ein Arbeitstitel genügt, um anzufangen. Alles Weitere liest DAMOKLES im nächsten Schritt aus den
        Unterlagen.
      </p>
      <div className={styles.formGrid}>
        <div className={styles.span6}>
          <TextField
            label="Arbeitstitel"
            required
            autoFocus
            value={draft.name}
            error={errors.name}
            hint="Intern sichtbar. Der Vertriebsname lässt sich bis zur Freigabe ändern."
            disabled={disabled}
            onChange={(event) => set('name', event.target.value)}
          />
        </div>
        <div className={styles.span3}>
          <TextField
            label="Straße und Hausnummer"
            value={draft.address}
            disabled={disabled}
            autoComplete="street-address"
            onChange={(event) => set('address', event.target.value)}
          />
        </div>
        <div className={styles.span1}>
          <TextField
            label="PLZ"
            value={draft.postalCode}
            inputMode="numeric"
            error={errors.postalCode}
            disabled={disabled}
            autoComplete="postal-code"
            onChange={(event) => set('postalCode', event.target.value)}
          />
        </div>
        <div className={styles.span2}>
          <TextField
            label="Ort"
            value={draft.city}
            disabled={disabled}
            autoComplete="address-level2"
            onChange={(event) => set('city', event.target.value)}
          />
        </div>
        <div className={`${styles.span6} ${styles.fieldBlock}`}>
          <span className={styles.fieldLabel} id="wizard-property-type">
            Objektart
          </span>
          <Segmented<PropertyType>
            items={PROPERTY_TYPES}
            value={draft.propertyType}
            onChange={(value) => set('propertyType', value)}
            label="Objektart"
            className={styles.segmented}
            aria-labelledby="wizard-property-type"
          />
        </div>
        <div className={styles.span4}>
          <DeveloperPicker
            value={draft.developer}
            onChange={(developer) => set('developer', developer)}
            error={errors.developerId}
            disabled={disabled}
          />
        </div>
        <div className={styles.span2}>
          <TextField
            label="Einheiten (geschätzt)"
            inputMode="numeric"
            value={draft.unitsEstimated}
            error={errors.unitsEstimated}
            hint="Wird aus der Teilungserklärung übernommen."
            disabled={disabled}
            onChange={(event) => set('unitsEstimated', event.target.value)}
          />
        </div>
      </div>
    </section>
  );
}

export interface BasicsSidebarProps {
  introducedBy: string;
  statusLabel: string;
}

/** Objekt anlegen 01 sidebar: „Sichtbarkeit" + „Ablauf". */
export function BasicsSidebar({ introducedBy, statusLabel }: BasicsSidebarProps) {
  return (
    <>
      <section className={styles.card} aria-labelledby="wizard-visibility">
        <Eyebrow id="wizard-visibility">Sichtbarkeit</Eyebrow>
        <div className={styles.sideText}>Bis zur Freigabe nur für Sie und Nutzer mit Ankaufsrecht.</div>
        <div className={styles.sideRows}>
          <div className={styles.sideRow}>
            <span className={styles.sideRowLabel}>Eingebracht von</span>
            <span className={styles.sideRowValue}>{introducedBy}</span>
          </div>
          <div className={styles.sideRow}>
            <span className={styles.sideRowLabel}>Status</span>
            <span className={styles.sideRowValue}>{statusLabel}</span>
          </div>
          <div className={styles.sideRow}>
            <span className={styles.sideRowLabel}>Zurückziehen</span>
            <span className={styles.sideRowValue}>Sie oder Superadmin</span>
          </div>
        </div>
      </section>
      <section className={`${styles.card} ${styles.cardSunken}`} aria-labelledby="wizard-flow">
        <Eyebrow id="wizard-flow">Ablauf</Eyebrow>
        <div className={styles.flowSteps}>
          <span>Unterlagen hochladen oder beim Bauträger anfordern.</span>
          <span>DAMOKLES ordnet sie zu und füllt Kalkulation und Checkliste vor.</span>
          <span>Freigabe, sobald alle Pflichtpunkte erfüllt sind und der Provisionssatz steht.</span>
        </div>
      </section>
    </>
  );
}
